import { LEAD_STAGE_CURRENT, NODE_DATA_LEAD_STAGE_KEY, type GraphNode, type GraphVisualizationFrame } from "@salesko/contracts";
import { Badge, Button, Card, EmptyState } from "@salesko/design-system";
import { ExternalLink, UserCheck, XCircle } from "lucide-react";
import type { CSSProperties } from "react";
import { useI18n } from "../i18n";
import { entityMeta } from "./entityMeta";
import { entitySingularLabel } from "./i18n";

type CurrentLeadsViewProps = {
  frame: GraphVisualizationFrame | null;
  onSelectNode: (nodeId: string) => void;
  onToggleLeadStage: (node: GraphNode, target: "current" | null) => void;
  readOnly: boolean;
};

/** Every node currently pinned to the Current lane (data.leadStage === "current"), regardless of entity type. */
export function currentLeadNodes(frame: GraphVisualizationFrame | null): GraphNode[] {
  if (!frame) {
    return [];
  }

  return frame.graph.nodes.filter((node) => node.data[NODE_DATA_LEAD_STAGE_KEY] === LEAD_STAGE_CURRENT);
}

const headerStyle: CSSProperties = {
  display: "grid",
  gap: "6px",
  borderBottom: "1px solid var(--border-subtle)",
  paddingBottom: "14px"
};

const eyebrowStyle: CSSProperties = {
  margin: 0,
  textTransform: "uppercase",
  letterSpacing: "var(--tracking-eyebrow)",
  fontFamily: "var(--font-mono)",
  fontSize: "var(--text-micro)",
  fontWeight: 700,
  color: "var(--text-muted)"
};

const titleRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "10px",
  flexWrap: "wrap"
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontFamily: "var(--font-display)",
  fontSize: "var(--text-title-lg)",
  fontWeight: 700,
  color: "var(--text-strong)",
  letterSpacing: "var(--tracking-tight)"
};

const countStyle: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "var(--text-micro)",
  color: "var(--text-muted)"
};

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "12px"
};

const rowBodyStyle: CSSProperties = {
  display: "grid",
  gap: "4px",
  flex: 1,
  minWidth: 0
};

const labelStyle: CSSProperties = {
  margin: 0,
  fontFamily: "var(--font-sans)",
  fontSize: "var(--text-body-md)",
  fontWeight: 700,
  color: "var(--text-strong)"
};

const metaStyle: CSSProperties = {
  margin: 0,
  color: "var(--text-muted)",
  fontSize: "var(--text-micro)"
};

const reasonStyle: CSSProperties = {
  margin: 0,
  color: "var(--text-secondary)",
  fontSize: "var(--text-body-sm)",
  fontStyle: "italic"
};

const actionsRowStyle: CSSProperties = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap"
};

export function CurrentLeadsView({ frame, onSelectNode, onToggleLeadStage, readOnly }: CurrentLeadsViewProps) {
  const { t } = useI18n();
  const nodes = currentLeadNodes(frame);
  const copy = t.currentLeads;

  return (
    <div className="current-leads-view">
      <header style={headerStyle}>
        <p style={eyebrowStyle}>{copy.eyebrow}</p>
        <div style={titleRowStyle}>
          <h2 style={titleStyle}>{copy.title}</h2>
          <span style={countStyle}>{copy.activeCount(nodes.length)}</span>
        </div>
      </header>

      {nodes.length > 0 ? (
        <div style={{ display: "grid", gap: "12px", marginTop: "16px" }}>
          {nodes.map((node) => (
            <CurrentLeadRow
              key={node.id}
              node={node}
              onSelectNode={onSelectNode}
              onToggleLeadStage={onToggleLeadStage}
              readOnly={readOnly}
            />
          ))}
        </div>
      ) : (
        <EmptyState icon={<UserCheck size={22} />} title={copy.emptyTitle}>
          {copy.emptyBody}
        </EmptyState>
      )}
    </div>
  );
}

function CurrentLeadRow({
  node,
  onSelectNode,
  onToggleLeadStage,
  readOnly
}: {
  node: GraphNode;
  onSelectNode: (nodeId: string) => void;
  onToggleLeadStage: (node: GraphNode, target: "current" | null) => void;
  readOnly: boolean;
}) {
  const { t } = useI18n();
  const copy = t.currentLeads;
  const meta = entityMeta[node.type];
  const email = stringDataField(node.data, "email");
  const leadSource = stringDataField(node.data, "leadSource");
  const classificationReason = stringDataField(node.data, "classificationReason");

  return (
    <Card style={{ display: "grid", gap: "8px" }}>
      <div style={rowStyle}>
        <Badge tone={meta.tone as "person" | "company" | "event" | "opportunity" | "note"} variant="dot" size="sm">
          {entitySingularLabel(t, node.type)}
        </Badge>
        <div style={rowBodyStyle}>
          <p style={labelStyle}>{node.label}</p>
          {email ? <p style={metaStyle}>{email}</p> : null}
          {leadSource ? <p style={metaStyle}>{leadSourceLabel(leadSource, copy)}</p> : null}
          {classificationReason ? <p style={reasonStyle}>{classificationReason}</p> : null}
        </div>
      </div>
      <div style={actionsRowStyle}>
        <Button iconLeft={<ExternalLink size={14} />} onClick={() => onSelectNode(node.id)} size="sm" variant="secondary">
          {copy.viewInGraph}
        </Button>
        {!readOnly ? (
          <Button
            iconLeft={<XCircle size={14} />}
            onClick={() => onToggleLeadStage(node, null)}
            size="sm"
            variant="ghost"
          >
            {copy.removeFromCurrent}
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

function leadSourceLabel(value: string, copy: { sourceEmail: string }) {
  return value === "email_connector" ? copy.sourceEmail : value;
}

function stringDataField(data: Record<string, unknown>, key: string) {
  const value = data[key];
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}
